import React from "react";
import { useNavigate } from "react-router-dom";

import { CREATE_COUPON_DEFAULT_VALUES, ZERO } from "constants/coupons";
import routes from "constants/routes";
import { buildCreateCouponPayload } from "helpers/couponApi";
import { formatApiErrorForDisplay } from "helpers/packageManagementApi";
import { useCreateCoupon } from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import CouponForm from "./CouponForm";

const normalizePackageId = (packageId) => String(packageId ?? "").trim();

const filterPayloadPackages = (payload, availablePackageIds = []) => {
  if (payload.applicable_packages.select_all) {
    return { payload, droppedPackageIds: [] };
  }

  const requestedPackageIds = payload.applicable_packages.package_ids;
  if (requestedPackageIds.length === ZERO) {
    return { payload, droppedPackageIds: [] };
  }

  const validPackageIdSet = new Set(
    availablePackageIds.map(normalizePackageId).filter(Boolean)
  );
  const filteredPackageIds = requestedPackageIds.filter((packageId) =>
    validPackageIdSet.has(normalizePackageId(packageId))
  );
  const droppedPackageIds = requestedPackageIds.filter(
    (packageId) => !validPackageIdSet.has(normalizePackageId(packageId))
  );

  if (filteredPackageIds.length === ZERO) {
    throw new Error(
      `No selected packages are available for the selected tenants.`
    );
  }

  return {
    payload: {
      ...payload,
      applicable_packages: {
        select_all: false,
        package_ids: filteredPackageIds,
      },
    },
    droppedPackageIds,
  };
};

const CreateCoupon = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const { mutateAsync, isPending } = useCreateCoupon();

  const handleSubmit = async (values, context = {}) => {
    try {
      const tenants = context.tenants ?? [];
      const availablePackageIds = context.availablePackageIds ?? [];
      const payload = buildCreateCouponPayload(values, { tenants });

      if (!payload) {
        openSnackbar(
          "Select tenants with a valid currency before creating a coupon.",
          "error"
        );
        return;
      }

      const { payload: scopedPayload, droppedPackageIds } = filterPayloadPackages(
        payload,
        availablePackageIds
      );

      if (droppedPackageIds.length > ZERO) {
        openSnackbar(
          `Some packages were unavailable for the selected tenants and were skipped: ${droppedPackageIds.join(", ")}`,
          "warning"
        );
      }

      const result = await mutateAsync(scopedPayload);

      openSnackbar(
        `Coupon "${result.coupon_code}" created successfully`,
        "success"
      );
      navigate(routes.coupons.view(result.coupon_code), { replace: true });
    } catch (error) {
      openSnackbar(
        formatApiErrorForDisplay(error, "Failed to create coupon."),
        "error"
      );
    }
  };

  return (
    <CouponForm
      mode="create"
      initialValues={CREATE_COUPON_DEFAULT_VALUES}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
    />
  );
};

export default CreateCoupon;
